// DashboardBabysitter.jsx (VERSÃO FINAL PROFISSIONAL)

import React, { useState, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { supabase, isApp } from "../supabaseClient";
import { uploadToStorage } from "../utils/uploadToStorage";
import {
  BABYSITTER_BASIC_SERVICES,
  BABYSITTER_EXTRA_SERVICES,
  sanitizeExtraServices,
} from "../shared/babysitterServices";
import AppShell from "../components/app-shell/AppShell";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import CalendarioAgenda from "../components/CalendarioAgenda";
import CompraPlanos from "../components/CompraPlanos";
import Modal from "../components/Modal";
import VerificadorIdentidade from "../auth/VerificadorIdentidade";
import DocumentsViewer from "../shared/documents/DocumentsViewer";
import { useCart } from "../shared/contexts/CartContext";
import toast from "react-hot-toast";
import {
  User,
  CreditCard,
  Shield,
  Upload,
  Check,
  Edit,
  Save,
  X,
  Trash2,
  Download,
  Calendar as CalendarIcon,
  ShieldAlert,
} from "lucide-react";

const BUCKET_NAME = "cadastros";
const PLACEHOLDER_BABA_URL =
  "https://placehold.co/300x300/e0e0e0/333333?text=BABA";

const tagsDisponiveis = [
  "Recém-nascido",
  "Bebês (1-2 anos)",
  "Pré-escolar",
  "Idade Escolar",
  "Autismo",
  "Necessidades Especiais",
];

const niveisOpcoes = ["Iniciante", "Intermediário", "Experiente (5+ anos)"];

const diasSemana = [
  { key: "domingo", label: "Dom" },
  { key: "segunda", label: "Seg" },
  { key: "terca", label: "Ter" },
  { key: "quarta", label: "Qua" },
  { key: "quinta", label: "Qui" },
  { key: "sexta", label: "Sex" },
  { key: "sabado", label: "Sáb" },
];

const periodosTrabalho = [
  { key: "manha", label: "Manhã" },
  { key: "tarde", label: "Tarde" },
  { key: "noite", label: "Noite" },
  { key: "pernoite", label: "Pernoite" },
];

const idiomasOpcoes = [
  "Português",
  "Inglês",
  "Espanhol",
  "Francês",
  "Libras",
  "Outro",
];

const servicosOpcoes = [
  ...BABYSITTER_EXTRA_SERVICES,
];

const newCertificateItem = () => ({
  id: uuidv4(),
  nome: "",
  url: null, // Usamos 'url' aqui para consistência com o que vem do banco
  previewUrl: null, // Para arquivos novos antes do upload
});

const getPathFromPublicUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  // Se for um path relativo (ex: 'fotos/arquivo.webp') retorna direto
  if (!url.startsWith("http") && url.includes("/")) return url;
  const regex = new RegExp(
    `\\/storage\\/v1\\/object\\/public\\/${BUCKET_NAME}\\/(.*)`,
  );
  const match = url.match(regex);
  return match ? match[1] : null;
};

export default function DashboardBabysitter() {
  const navigate = useNavigate();
  useCart();

  const [loading, setLoading] = useState(true);
  const [perfil, setPerfil] = useState(null);
  const [perfilEdit, setPerfilEdit] = useState(null);
  const [activeTab, setActiveTab] = useState("inicio");
  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [familyDetails, setFamilyDetails] = useState(null);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [trabalhos, setTrabalhos] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [displayFotoUrl, setDisplayFotoUrl] = useState(null);
  const [isVerificadorOpen, setIsVerificadorOpen] = useState(false);
  const [extratoUnificadoBaba, setExtratoUnificadoBaba] = useState([]); // New state for babysitter's extract
  const [tags, setTags] = useState([]);
  const [disponibilidade, setDisponibilidade] = useState({});
  const [certificados, setCertificados] = useState([]);

  const toggleArrayItem = (field, item) => {
    if (!isEditing) return;
    setPerfilEdit((prev) => {
      const arr = prev[field] || [];
      const newArr = arr.includes(item)
        ? arr.filter((i) => i !== item)
        : [...arr, item];
      return { ...prev, [field]: newArr };
    });
  };

  const handleReferenceChange = (index, field, value) => {
    setPerfilEdit((prev) => {
      const newRefs = [...(prev.referencias_dados || [])];
      if (!newRefs[index]) newRefs[index] = { nome: "", contato: "" };
      newRefs[index] = { ...newRefs[index], [field]: value };
      return { ...prev, referencias_dados: newRefs };
    });
  };

  const handleCertificateEdit = (index, field, value) => {
    setCertificados((prev) => {
      const newCerts = [...prev];
      if (!newCerts[index]) return prev;
      newCerts[index] = { ...newCerts[index], [field]: value };
      return newCerts;
    });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPerfilEdit((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value ?? 0);

  const getMensagemCrescimento = () => {
    if (ganhoAnterior === 0 && ganhoAtual === 0) {
      return "Comece a trabalhar e gere seus primeiros ganhos 🚀";
    }

    if (ganhoAnterior === 0) {
      return "Ótimo começo! Você já está gerando ganhos 💰";
    }

    if (crescimento > 0) {
      return `Você cresceu +${crescimento.toFixed(0)}% este mês 🚀`;
    }

    if (crescimento < 0) {
      return `Seus ganhos caíram ${crescimento.toFixed(
        0,
      )}% — veja oportunidades`;
    }

    return "Seus ganhos estão estáveis este mês";
  };

  const fetchAuthorizedPhotoUrl = useCallback(async (foto_url) => {
    if (!foto_url || foto_url.includes("placehold.co")) {
      setDisplayFotoUrl(foto_url || PLACEHOLDER_BABA_URL);
      return;
    }

    // Links externos passam sem alteração.
    if (foto_url.startsWith("http") && !foto_url.includes("supabase.co")) {
      setDisplayFotoUrl(foto_url);
      return;
    }

    // Para paths ou URLs do Supabase, obter URL pública
    const filePath = getPathFromPublicUrl(foto_url);
    if (filePath) {
      const { data } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(filePath);
      setDisplayFotoUrl(data.publicUrl);
    } else {
      setDisplayFotoUrl(foto_url);
    }
  }, []);

  // =============================
  // FETCH PERFIL
  // =============================
  const fetchPerfil = useCallback(async (userId) => {

    try {
      setLoading(true);

      const { data, error } = await supabase.rpc("get_complete_profile", {
        p_user_id: userId,
      });

      if (error) throw error;

      // Se a babá nunca preencheu o formulário de cadastro, redireciona para lá
      if (!data?.detalhes) {
        navigate("/cadastro-baba", { replace: true });
        return;
      }

      const formatted = {
        ...data.usuario,
        ...data.detalhes,
        servicos_extras: sanitizeExtraServices(data.detalhes?.servicos_extras),
        avgRating: data.avgRating || 0,
      };

      setPerfil(formatted);
      setPerfilEdit(formatted);

      // Dispara as buscas de dados dependentes do perfil
      await fetchTrabalhos(userId);
      await fetchExtratoBaba(userId);

      // Sincroniza estados com os nomes das colunas do banco
      // Garante que 'area_atuacao' é sempre um array antes de ser usado
      setTags(
        Array.isArray(formatted.area_atuacao)
          ? formatted.area_atuacao
          : typeof formatted.area_atuacao === "string" &&
            formatted.area_atuacao.startsWith("[") &&
            formatted.area_atuacao.endsWith("]")
          ? JSON.parse(formatted.area_atuacao)
          : formatted.area_atuacao || [], // Garante que seja um array vazio se nulo
      );
      setDisponibilidade(formatted.disponibilidade || {});

      // Resolve os caminhos dos certificados para URLs públicas para exibição
      const resolvedCerts = (formatted.certificados_docs || []).map((cert) => {
        if (cert.url && !cert.url.startsWith("http")) {
          const { data: urlData } = supabase.storage
            .from("documentos")
            .getPublicUrl(cert.url);
          return { ...cert, url: urlData.publicUrl };
        }
        return cert;
      });
      setCertificados(resolvedCerts);
    } catch (err) {
      console.error("Erro ao buscar perfil:", err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return navigate("/login");

      // Garante que as buscas ocorram usando o ID da sessão, sem esperar o estado 'perfil'
      if (!perfil) {
        fetchTrabalhos(session.user.id);
        fetchExtratoBaba(session.user.id);
      }

      fetchPerfil(session.user.id);
    });
  }, [fetchPerfil, navigate]);

  useEffect(() => {
    if (perfil?.foto_url) {
      fetchAuthorizedPhotoUrl(perfil.foto_url);
    } else if (perfil && !perfil.foto_url) {
      setDisplayFotoUrl(PLACEHOLDER_BABA_URL);
    }
  }, [perfil, fetchAuthorizedPhotoUrl]);

  // =============================
  // FETCH TRABALHOS
  // =============================
  const fetchTrabalhos = useCallback(
    async (userId) => {
      const idBusca = userId || perfil?.id;
      if (!idBusca) return;
      const { data } = await supabase
        .from("vw_agendamentos_detalhados")
        .select("*")
        .eq("baba_id", idBusca)
        .order("inicio", { ascending: true });

      setTrabalhos(data || []);
    },
    [perfil?.id],
  );

  // New fetch function for babysitter's extract
  const fetchExtratoBaba = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase.rpc("get_extrato_baba", {
        p_user_id: userId,
      });

      if (error) {
        console.warn(
          "Função get_extrato_baba não encontrada no banco. Crie-a no SQL Editor.",
        );
        return;
      }
      setExtratoUnificadoBaba(data || []);
    } catch (err) {
      console.error("Erro ao buscar extrato da babá via RPC:", err);
    }
  }, []);

  const handleConfirmBlock = async (periodo) => {
    if (!selectedDate) return;

    let start = new Date(selectedDate);
    let end = new Date(selectedDate);
    let obs = "Bloqueio manual: ";

    switch (periodo) {
      case "manha":
        start.setHours(8, 0, 0);
        end.setHours(12, 0, 0);
        obs += "Manhã";
        break;
      case "tarde":
        start.setHours(12, 0, 0);
        end.setHours(18, 0, 0);
        obs += "Tarde";
        break;
      case "noite":
        start.setHours(18, 0, 0);
        end.setHours(22, 0, 0);
        obs += "Noite";
        break;
      case "pernoite":
        start.setHours(22, 0, 0);
        end.setDate(end.getDate() + 1);
        end.setHours(8, 0, 0);
        obs += "Pernoite";
        break;
      default: // Dia Todo
        start.setHours(8, 0, 0);
        end.setHours(22, 0, 0);
        obs += "Dia Inteiro";
    }

    try {
      const { error } = await supabase.from("agendamentos").insert([
        {
          baba_id: perfil.id,
          familia_id: perfil.id,
          inicio: start.toISOString(),
          fim: end.toISOString(),
          status: "confirmado",
          observacoes: obs,
        },
      ]);

      if (error) throw error;
      toast.success("Horário bloqueado!");
      setIsBlockModalOpen(false);
      fetchTrabalhos();
    } catch {
      toast.error("Erro ao bloquear horário.");
    }
  };

  const handleSelectSlot = (slot) => {
    setSelectedDate(new Date(slot.start));
    setIsBlockModalOpen(true);
  };

  const handleSelectEvent = (event) => {
    setEventoSelecionado(event);
    if (event.familia_id !== event.baba_id) {
      fetchFamilyDetails(event.familia_id);
    } else {
      setFamilyDetails(null);
    }
  };

  const fetchFamilyDetails = useCallback(async (familiaId) => {
    try {
      const { data, error } = await supabase.rpc("get_complete_profile", {
        p_user_id: familiaId,
      });

      if (error) throw error;

      // Mescla os dados do usuário (endereço, nome) com os detalhes do cliente
      if (data && data.usuario) {
        setFamilyDetails({ ...data.usuario, ...data.detalhes });
      }
    } catch (err) {
      console.error("Erro ao buscar detalhes da família:", err);
    }
  }, []);

  const handleConfirmarTrabalho = async (id) => {
    try {
      const { data, error } = await supabase.rpc(
        "rpc_atualizar_status_agendamento",
        {
          p_agendamento_id: id,
          p_novo_status: "confirmado",
        },
      );

      if (error) throw error;
      if (data?.erro) {
        toast.error(data.erro);
      } else {
        toast.success("✅ Agendamento confirmado!");
        fetchTrabalhos(perfil.id);
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao confirmar agendamento.");
    }
  };

  const handleRecusarTrabalho = async (id) => {
    if (!window.confirm("Deseja realmente recusar este agendamento?")) return;
    try {
      const { data, error } = await supabase.rpc(
        "rpc_atualizar_status_agendamento",
        {
          p_agendamento_id: id,
          p_novo_status: "recusado",
        },
      );

      if (error) throw error;
      if (data?.erro) {
        toast.error(data.erro);
      } else {
        toast.error("Agendamento recusado.");
        fetchTrabalhos(perfil.id);
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao processar solicitação.");
    }
  };

  // Push Notification Registration
  const registerPushNotifications = useCallback(async () => {
    if (isApp()) {
      try {
        const { PushNotifications } = await import(
          "@capacitor/push-notifications"
        );

        let perm = await PushNotifications.checkPermissions();
        if (perm.receive !== "granted") {
          perm = await PushNotifications.requestPermissions();
        }
        if (perm.receive !== "granted") return;

        await PushNotifications.register();

        PushNotifications.addListener("registration", async (token) => {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          await fetch("/api/push/subscribe", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ token: token.value, platform: "native" }),
          });
        });

        PushNotifications.addListener(
          "pushNotificationReceived",
          (notification) => {
            toast.success(notification.title + ": " + notification.body);
          },
        );
      } catch (err) {
        console.error("Erro push nativo:", err);
      }
    } else if ("serviceWorker" in navigator && "PushManager" in window) {
      try {
        // Comentado para evitar erro de MIME type até que o arquivo public/sw.js seja criado

        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;
        const registration = await navigator.serviceWorker.register("/sw.js");
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            import.meta.env.VITE_VAPID_PUBLIC_KEY, // Use sempre a variável de ambiente
          ),
        });
        const {
          data: { session },
        } = await supabase.auth.getSession();
        await fetch("/api/push/subscribe", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ subscription }),
        });

        console.log("Service Worker registration skipped: sw.js not found.");
      } catch (err) {
        console.error("Error web push:", err);
      }
    }
  }, []);

  // Helper function for VAPID key
  function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  useEffect(() => {
    if (perfil?.id) {
      registerPushNotifications();
    }
  }, [perfil?.id, registerPushNotifications]);

  useEffect(() => {
    if (!perfil?.id) return;

    const channel = supabase
      .channel("agendamentos-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "agendamentos",
          filter: `baba_id=eq.${perfil.id}`,
        },
        (payload) => {
          console.log("Mudança em tempo real:", payload);

          // 🔔 notificação
          if (payload.eventType === "INSERT" || payload.new) {
            toast.success("📢 Nova solicitação de agendamento!");
          }

          // 🔄 recarregar agenda
          fetchTrabalhos();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [perfil?.id, fetchTrabalhos]);

  const handleCancelarAgendamento = async (id) => {
    if (!window.confirm("Tem certeza que deseja cancelar este trabalho?"))
      return;

    try {
      const { data, error } = await supabase.rpc("cancelar_agendamento", {
        p_agendamento_id: id,
        p_user_id: perfil.id,
      });

      if (error) throw error;

      if (data?.erro) {
        toast.error(data.erro);
      } else {
        toast.success(data.sucesso || "Trabalho cancelado com sucesso.");
        fetchTrabalhos(); // Atualiza a lista de trabalhos
      }
    } catch (err) {
      console.error("Erro ao cancelar agendamento:", err);
      toast.error("Não foi possível cancelar o agendamento.");
    }
  };

  const trabalhosReais = trabalhos.filter((t) => t.familia_id !== t.baba_id);

  const trabalhosMesAtual = trabalhosReais.filter((t) => {
    const data = new Date(t.inicio || t.data_inicio);
    const now = new Date();
    return (
      data.getMonth() === now.getMonth() &&
      data.getFullYear() === now.getFullYear()
    );
  });

  const trabalhosMesAnterior = trabalhosReais.filter((t) => {
    const data = new Date(t.inicio || t.data_inicio);
    const prevMonth = new Date();
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    return (
      data.getMonth() === prevMonth.getMonth() &&
      data.getFullYear() === prevMonth.getFullYear()
    );
  });

  const calcularGanhos = (lista) => {
    // Calcula ganhos apenas para trabalhos que já foram aceitos ou finalizados
    return (
      lista.filter((t) => t.status === "confirmado" || t.status === "concluido")
        .length * 80
    );
  };

  const ganhoAtual = calcularGanhos(trabalhosMesAtual);
  const ganhoAnterior = calcularGanhos(trabalhosMesAnterior);

  const crescimento =
    ganhoAnterior === 0
      ? 100
      : ((ganhoAtual - ganhoAnterior) / ganhoAnterior) * 100;

  const getCorCrescimento = () => {
    if (crescimento > 0) return "green";
    if (crescimento < 0) return "red";
    return "gray";
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-40 rounded-2xl bg-warm-200" />
        <div className="h-24 rounded-2xl bg-warm-200" />
        <div className="h-24 rounded-2xl bg-warm-200" />
        <div className="h-24 rounded-2xl bg-warm-200" />
      </div>
    );
  }

  // =============================
  // HERO
  // =============================
  const renderHero = () => (
    <div className="relative overflow-hidden rounded-(--radius-xl) bg-linear-to-br from-rose-400 to-rose-500 text-white p-6 mb-6 shadow-(--shadow-hover)">
      {/* Círculo decorativo de fundo */}
      <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-white/10" />
      <div className="absolute -bottom-6 -right-2 h-24 w-24 rounded-full bg-white/10" />

      <div className="relative flex items-center gap-4">
        <Avatar size="lg" className="shrink-0 border-4 border-white/40">
          <AvatarImage src={displayFotoUrl} alt={perfil?.nome} />
          <AvatarFallback>{(perfil?.nome || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-white/70 text-sm font-medium">Bem-vinda de volta</p>
          <h1 className="text-2xl font-heading font-bold truncate">
            {perfil?.nome} 👋
          </h1>
          <p className="text-white/80 text-sm mt-0.5">{getMensagemCrescimento()}</p>
        </div>
        <div className="shrink-0 text-center hidden sm:block">
          <span className="text-3xl font-bold">{trabalhos.length}</span>
          <p className="text-white/70 text-xs">trabalhos<br />este mês</p>
        </div>
      </div>

      <div className="relative mt-4 flex gap-2 flex-wrap">
        <button
          onClick={() => setActiveTab("perfil")}
          className="px-4 py-1.5 rounded-full bg-white text-rose-500 text-sm font-semibold hover:bg-white/90 transition-colors"
        >
          {perfil?.perfil_completo ? "Meu Perfil" : "Completar Perfil"}
        </button>
        {!perfil?.perfil_completo && (
          <span className="px-3 py-1.5 rounded-full bg-white/20 text-white text-xs font-medium">
            Perfil incompleto
          </span>
        )}
      </div>
    </div>
  );

  // =============================
  // INICIO
  // =============================
  const renderInicio = () => {
    const eventos = trabalhos.map((t) => ({
      id: t.id,
      title:
        t.familia_id === t.baba_id
          ? "🔒 Bloqueio Manual"
          : `🏡 ${t.familia_nome || "Família"}`,
      start: new Date(t.inicio || t.data_inicio),
      end: new Date(t.fim || t.data_fim),
      status: t.status,
      familia_id: t.familia_id,
      baba_id: t.baba_id,
    }));

    const proximoTrabalho = (trabalhos || []).find(
      (t) =>
        new Date(t.inicio || t.data_inicio) >= new Date() &&
        t.familia_id !== t.baba_id,
    );

    const statusLabel = { agendado: "Solicitado", confirmado: "Confirmado", concluido: "Concluído", cancelado: "Cancelado" };
    const STATUS_BADGE_CLASS = {
      agendado: "bg-amber-100 text-amber-700",
      confirmado: "bg-blue-100 text-blue-700",
      concluido: "bg-muted text-muted-foreground",
      cancelado: "bg-red-100 text-red-600",
    };

    return (
      <div id="baba-view-home" className="space-y-5">

        {/* Alerta documentos pendentes */}
        {perfil?.doc_status === "incompleto" && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-100 border border-amber-400 text-amber-700">
            <ShieldAlert size={22} className="shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Perfil inativo</p>
              <p className="text-xs mt-0.5 opacity-90">Envie seus documentos para aparecer nas buscas.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setIsVerificadorOpen(true)}
              className="shrink-0 border-amber-700 text-amber-700 hover:bg-amber-100">
              Enviar
            </Button>
          </div>
        )}

        {/* Hero */}
        {renderHero()}

        {/* Cards de métricas */}
        <div className="grid grid-cols-3 gap-3">
          {/* Próximo trabalho */}
          <Card className="col-span-3 sm:col-span-1">
            <CardContent>
              <div className="flex items-center gap-2 mb-2">
                <span className="p-1.5 rounded-sm bg-secondary/10">
                  <CalendarIcon size={16} className="text-secondary" />
                </span>
                <span className="text-xs font-medium text-muted-foreground">Próximo trabalho</span>
              </div>
              <p className="text-sm font-semibold text-foreground leading-snug">
                {proximoTrabalho
                  ? `${new Date(proximoTrabalho.inicio || proximoTrabalho.data_inicio).toLocaleDateString()} — ${proximoTrabalho.familia_nome}`
                  : "Nenhum agendado"}
              </p>
            </CardContent>
          </Card>

          {/* Horas no mês */}
          <Card className="col-span-3 sm:col-span-1">
            <CardContent>
              <div className="flex items-center gap-2 mb-2">
                <span className="p-1.5 rounded-sm bg-primary/10">
                  <Shield size={16} className="text-primary" />
                </span>
                <span className="text-xs font-medium text-muted-foreground">Horas no mês</span>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {trabalhosMesAtual.length * 4}
                <span className="text-sm font-normal text-muted-foreground ml-1">h</span>
              </p>
            </CardContent>
          </Card>

          {/* Ganhos */}
          <Card className="col-span-3 sm:col-span-1 border-l-4 border-l-primary">
            <CardContent>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 rounded-sm bg-primary/10">
                    <CreditCard size={16} className="text-primary" />
                  </span>
                  <span className="text-xs font-medium text-muted-foreground">Ganhos este mês</span>
                </div>
                <span className={`text-xs font-bold ${getCorCrescimento() === "green" ? "text-emerald-600" : getCorCrescimento() === "red" ? "text-red-600" : "text-muted-foreground"}`}>
                  {crescimento > 0 ? "+" : ""}{crescimento.toFixed(0)}%
                </span>
              </div>
              <p className="text-2xl font-bold text-foreground">
                R$ {ganhoAtual.toFixed(2)}
              </p>
              <button onClick={() => setActiveTab("faturamento")}
                className="mt-2 text-xs text-secondary font-medium hover:underline">
                Ver extrato →
              </button>
            </CardContent>
          </Card>
        </div>

        {/* Agenda */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CalendarIcon size={18} className="text-primary" />
              <CardTitle>Minha Agenda</CardTitle>
            </div>
          </CardHeader>

          <CardContent>
          <CalendarioAgenda
            eventos={eventos}
            user={perfil}
            onSelectSlot={handleSelectSlot}
            onSelectDay={(date) => { setSelectedDate(date); setIsBlockModalOpen(true); }}
            onSelectEvent={handleSelectEvent}
          />

          {/* Próximos compromissos */}
          {trabalhos.filter((t) => new Date(t.inicio || t.data_inicio) >= new Date() && t.familia_id !== t.baba_id).length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Próximos compromissos
              </p>
              {trabalhos
                .filter((t) => new Date(t.inicio || t.data_inicio) >= new Date() && t.familia_id !== t.baba_id)
                .slice(0, 3)
                .map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 p-3 rounded-md bg-muted border border-border">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">🏠 {t.familia_nome}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        📅 {new Date(t.inicio || t.data_inicio).toLocaleDateString()} às{" "}
                        {new Date(t.inicio || t.data_inicio).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Badge variant="outline" className={STATUS_BADGE_CLASS[t.status] || ""}>
                          {statusLabel[t.status] || t.status}
                        </Badge>
                        <button onClick={() => navigate(`/perfil-familia/${t.familia_id}`)}
                          className="text-xs text-secondary hover:underline flex items-center gap-0.5">
                          <User size={10} /> Perfil
                        </button>
                        <button onClick={() => handleSelectEvent({ id: t.id, title: `🏡 ${t.familia_nome}`, start: new Date(t.inicio), end: new Date(t.fim), status: t.status, familia_id: t.familia_id, baba_id: t.baba_id })}
                          className="text-xs text-secondary hover:underline flex items-center gap-0.5">
                          <Edit size={10} /> Detalhes
                        </button>
                      </div>
                    </div>
                    {t.status === "agendado" && (
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <Button size="sm" onClick={() => handleConfirmarTrabalho(t.id)}
                          className="h-7! px-3! text-xs! bg-emerald-600! hover:bg-emerald-700!">
                          Aceitar
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleRecusarTrabalho(t.id)}
                          className="h-7! px-3! text-xs!">
                          Recusar
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // =============================
  // PERFIL
  // =============================
  const progressoPerfil = () => {
    let total = 6;
    let preenchido = 0;

    if (perfil?.nome) preenchido++;
    if (perfil?.telefone) preenchido++;
    if (perfil?.cidade) preenchido++;
    if (perfil?.experiencia_text) preenchido++;
    if (tags.length) preenchido++;
    if (certificados.length) preenchido++;

    return Math.round((preenchido / total) * 100);
  };

  const toggleTag = (tag) => {
    if (!isEditing) return;
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const toggleDisponibilidade = (dia, periodo) => {
    if (!isEditing) return;
    setDisponibilidade((prev) => ({
      ...prev,
      [dia]: {
        // Garante que o objeto do dia exista
        ...prev[dia],
        [periodo]: !prev[dia]?.[periodo],
      },
    }));
  };

  const toggleDiaTodo = (dia) => {
    if (!isEditing) return;
    setDisponibilidade((prev) => {
      const estadoAtualDia = prev[dia] || {}; // Garante que o objeto do dia exista
      const todosAtivos = periodosTrabalho.every((p) => estadoAtualDia[p.key]); // Verifica se todos estão ativos
      const novoEstadoDia = {};
      periodosTrabalho.forEach((p) => {
        novoEstadoDia[p.key] = !todosAtivos; // Inverte o estado para todos os períodos
      });
      return { ...prev, [dia]: novoEstadoDia };
    }); // Atualiza o estado de disponibilidade
  };

  const handleFotoUpload = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;

      const previewUrl = URL.createObjectURL(file);
      setDisplayFotoUrl((prev) => {
        if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
        return previewUrl;
      });

      const { path, publicUrl } = await uploadToStorage(file, "baba");
      URL.revokeObjectURL(previewUrl);
      setPerfilEdit((prev) => ({ ...prev, foto_url: path }));
      setDisplayFotoUrl(publicUrl);
    } catch (err) {
      console.error("Erro upload:", err);
      toast.error("Erro ao fazer upload da foto.");
    }
  };

  const handleAddCertificateField = () => {
    if (certificados.length < 5) {
      setCertificados((prev) => [...prev, newCertificateItem()]);
    } else {
      toast.error("Você pode adicionar no máximo 5 certificados.");
    }
  };

  const handleRemoveCertificateField = (idToRemove) => {
    const confirmar = window.confirm(
      "Tem certeza que deseja remover este certificado? A exclusão será definitiva após clicar em Salvar.",
    );
    if (confirmar) {
      setCertificados((prev) => prev.filter((cert) => cert.id !== idToRemove));
    }
  };

  const handleCertificateFileChange = (id, file) => {
    setCertificados((prev) =>
      prev.map((cert) =>
        cert.id === id
          ? { ...cert, file, previewUrl: URL.createObjectURL(file) }
          : cert,
      ),
    );
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Garante que certificados não tenham o campo file (que é um objeto binário) para não quebrar o JSON do RPC
      const certificadosParaSalvar = certificados.map(
        // eslint-disable-next-line no-unused-vars
        ({ file: _file, previewUrl: _previewUrl, ...rest }) => rest,
      );

      const areaAtuacaoLimpada = Array.isArray(tags) ? tags : [];

      const { error } = await supabase.rpc("rpc_save_user_profile", {
        p_user_id: perfil.id,
        p_basic_data: {
          nome: perfilEdit.nome,
          sobrenome: perfilEdit.sobrenome,
          telefone: perfilEdit.telefone,
          cidade: perfilEdit.cidade,
          estado: perfilEdit.estado,
          cep: perfilEdit.cep,
          endereco: perfilEdit.endereco,
          bairro: perfilEdit.bairro,
          numero: perfilEdit.numero,
          complemento: perfilEdit.complemento,
          bio: perfilEdit.bio,
          banco: perfilEdit.banco,
          agencia: perfilEdit.agencia,
          conta: perfilEdit.conta,
          pix: perfilEdit.pix,
          foto_url: perfilEdit.foto_url,
        },
        p_extra_data: {
          cpf: perfilEdit.cpf,
          data_nascimento: perfilEdit.data_nascimento || null,
          experiencia_text: perfilEdit.experiencia_text,
          qualificacoes_text: perfilEdit.qualificacoes_text,
          nivel_experiencia: perfilEdit.nivel_experiencia,
          aceita_emergencial: perfilEdit.aceita_emergencial,
          raio_atendimento_km: perfilEdit.raio_atendimento_km,
          idiomas_falados: perfilEdit.idiomas_falados,
          servicos_extras: sanitizeExtraServices(perfilEdit.servicos_extras),
          referencias_dados: perfilEdit.referencias_dados,
          rede_social: perfilEdit.rede_social,
          area_atuacao: areaAtuacaoLimpada,
          disponibilidade,
          certificados_docs: certificadosParaSalvar,
        },
        p_role: "baba",
      });

      if (error) throw error;

      // 3. Sincronizar estado local
      setPerfil({
        ...perfilEdit,
        area_atuacao: tags,
        disponibilidade,
        certificados_docs: certificados,
      });
      setIsEditing(false);
      toast.success("Perfil atualizado com sucesso!");
    } catch (err) {
      console.error("Erro ao salvar perfil:", err);
      toast.error("Ocorreu um erro ao salvar as alterações.");
    } finally {
      setLoading(false);
    }
  };

  // =============================
  // PERFIL
  // =============================

  const renderPerfil = () => {
    const pct = progressoPerfil();
    const inputCls = "w-full border border-warm-300 rounded-[var(--radius-md)] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 transition-colors";
    const textareaCls = `${inputCls} resize-none`;
    const tagBase = "px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-150 select-none";
    const tagActive = "bg-rose-400 text-white border-rose-400";
    const tagInactive = "bg-white text-warm-600 border-warm-300 hover:border-rose-300";

    return (
      <div id="baba-view-profile" className="space-y-4">

        {/* Barra de ações de edição — sticky no topo */}
        {isEditing && (
          <div className="sticky top-14 z-20 flex items-center justify-between gap-3 px-4 py-3 bg-white border border-warm-200 rounded-lg shadow-(--shadow-hover)">
            <p className="text-sm font-medium text-warm-700">Editando perfil</p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setIsEditing(false); setPerfilEdit(perfil); }}><X size={14} />Cancelar</Button>
              <Button size="sm" onClick={handleSave} disabled={loading}><Save size={14} />{loading ? "Salvando..." : "Salvar"}</Button>
            </div>
          </div>
        )}

        {/* Hero do perfil */}
        <Card>
          <CardContent className="flex items-start gap-4">
            {/* Foto */}
            <div className="relative shrink-0">
              <Avatar className="size-20 ring-2 ring-primary">
                <AvatarImage src={displayFotoUrl} alt={perfil.nome} />
                <AvatarFallback>{(perfil.nome || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              {isEditing && (
                <label htmlFor="foto-upload-dash" className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full cursor-pointer">
                  <Edit size={18} className="text-white" />
                  <input id="foto-upload-dash" type="file" accept="image/*" onChange={handleFotoUpload} hidden />
                </label>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="flex gap-2 mb-2">
                  <input id="baba-profile-input-firstname" className={inputCls} name="nome" value={perfilEdit.nome || ""} onChange={handleEditChange} placeholder="Nome" />
                  <input id="baba-profile-input-lastname" className={inputCls} name="sobrenome" value={perfilEdit.sobrenome || ""} onChange={handleEditChange} placeholder="Sobrenome" />
                </div>
              ) : (
                <h2 id="baba-profile-fullname" className="text-xl font-heading font-bold text-warm-900">
                  {perfil.nome} {perfil.sobrenome}
                </h2>
              )}
              <div className="flex flex-wrap gap-3 mt-1 text-sm text-warm-500">
                <span>⭐ {perfil?.avgRating?.toFixed(1) || "0.0"} ({perfil?.countReviews || 0} avaliações)</span>
                <span>📍 {perfil.cidade}</span>
              </div>

              {/* Barra de progresso */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-warm-500">Perfil completo</span>
                  <span className="text-xs font-semibold text-rose-400">{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-warm-200 overflow-hidden">
                  <div className="h-full rounded-full bg-rose-400 transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </div>

            {!isEditing && (
              <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} className="shrink-0"><Edit size={14} />Editar</Button>
            )}
          </CardContent>
        </Card>

        {/* Dados Pessoais */}
        <Card id="baba-profile-personal-data">
          <CardHeader><CardTitle>Dados Pessoais</CardTitle></CardHeader>
          <CardContent>
          {isEditing ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-warm-600 mb-1 block">CPF</label><input id="baba-profile-input-cpf" className={inputCls} name="cpf" value={perfilEdit.cpf || ""} onChange={handleEditChange} /></div>
              <div><label className="text-xs font-medium text-warm-600 mb-1 block">Telefone</label><input id="baba-profile-input-phone" className={inputCls} name="telefone" value={perfilEdit.telefone || ""} onChange={handleEditChange} /></div>
              <div><label className="text-xs font-medium text-warm-600 mb-1 block">Data Nascimento</label><input id="baba-profile-input-birthdate" className={inputCls} type="date" name="data_nascimento" value={perfilEdit.data_nascimento || ""} onChange={handleEditChange} /></div>
            </div>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              {[["CPF", perfil.cpf], ["Telefone", perfil.telefone], ["Nascimento", perfil.data_nascimento ? new Date(perfil.data_nascimento).toLocaleDateString("pt-BR") : null]].map(([label, val]) => (
                <div key={label}><dt className="text-xs text-warm-400 mb-0.5">{label}</dt><dd className="font-medium text-warm-800">{val || "Não informado"}</dd></div>
              ))}
            </dl>
          )}
        </CardContent>
        </Card>

        {/* Endereço */}
        <Card id="baba-profile-address-card">
          <CardHeader><CardTitle>Endereço</CardTitle></CardHeader>
          <CardContent>
          {isEditing ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[["CEP","cep","CEP"],["Rua","endereco","Rua"],["Nº","numero","Nº"],["Complemento","complemento","Comp."],["Bairro","bairro","Bairro"],["Cidade","cidade","Cidade"]].map(([label, name, ph]) => (
                <div key={name}><label className="text-xs font-medium text-warm-600 mb-1 block">{label}</label><input className={inputCls} name={name} value={perfilEdit[name] || ""} onChange={handleEditChange} placeholder={ph} /></div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-warm-700">
              {perfil.endereco || "Não informado"}, {perfil.numero || "S/N"}{perfil.complemento ? ` (${perfil.complemento})` : ""} — {perfil.bairro}, {perfil.cidade} - {perfil.estado} · CEP {perfil.cep || "—"}
            </p>
          )}
        </CardContent>
        </Card>

        {/* Sobre / Experiência / Qualificações */}
        {[
          { id: "bio-card", title: "Sobre mim", field: "bio", display: perfil.bio, placeholder: "Sem biografia cadastrada." },
          { id: "exp-card", title: "Experiência", field: "experiencia_text", display: perfil.experiencia_text, placeholder: "Não informada." },
          { id: "qual-card", title: "Qualificações", field: "qualificacoes_text", display: perfil.qualificacoes_text, placeholder: "Não informadas." },
        ].map(({ id, title, field, display, placeholder }) => (
          <Card key={id} id={`baba-profile-${id}`}>
            <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
            <CardContent>
            {isEditing
              ? <textarea className={textareaCls} name={field} value={perfilEdit[field] || ""} onChange={handleEditChange} rows={3} />
              : <p className="text-sm text-warm-700 leading-relaxed">{display || placeholder}</p>
            }
          </CardContent>
          </Card>
        ))}

        {/* Detalhes Profissionais */}
        <Card id="baba-profile-details-card">
          <CardHeader><CardTitle>Detalhes Profissionais</CardTitle></CardHeader>
          <CardContent>
          {isEditing ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-warm-600 mb-1 block">Nível</label>
                <select id="baba-profile-select-level" className={inputCls} name="nivel_experiencia" value={perfilEdit.nivel_experiencia} onChange={handleEditChange}>
                  {niveisOpcoes.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-warm-600 mb-1 block">Privacidade</label>
                <select id="baba-profile-select-privacy" className={inputCls} name="privacidade" value={perfilEdit.privacidade || "publico"} onChange={handleEditChange}>
                  <option value="publico">Público</option>
                  <option value="privado">Privado</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-warm-600 mb-1 block">Raio Atendimento (km)</label>
                <input id="baba-profile-input-radius" className={inputCls} type="number" name="raio_atendimento_km" value={perfilEdit.raio_atendimento_km} onChange={handleEditChange} />
              </div>
              <label className="flex items-center gap-2 text-sm text-warm-700 cursor-pointer col-span-full">
                <input id="checkbox-baba" type="checkbox" name="aceita_emergencial" checked={perfilEdit.aceita_emergencial} onChange={handleEditChange} className="accent-rose-400 h-4 w-4" />
                Aceita atendimento emergencial
              </label>
            </div>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              {[["Nível", perfil.nivel_experiencia], ["Raio", perfil.raio_atendimento_km ? `${perfil.raio_atendimento_km} km` : null], ["Emergencial", perfil.aceita_emergencial ? "Sim" : "Não"]].map(([label, val]) => (
                <div key={label}><dt className="text-xs text-warm-400 mb-0.5">{label}</dt><dd className="font-medium text-warm-800">{val || "Não informado"}</dd></div>
              ))}
            </dl>
          )}
        </CardContent>
        </Card>

        {/* Especialidades */}
        <Card id="baba-profile-area-card">
          <CardHeader><CardTitle>Especialidades</CardTitle></CardHeader>
          <CardContent>
          <div className="flex flex-wrap gap-2">
            {isEditing
              ? tagsDisponiveis.map((t) => (
                  <span key={t} onClick={() => toggleTag(t)} className={`${tagBase} cursor-pointer ${tags.includes(t) ? tagActive : tagInactive}`}>{t}</span>
                ))
              : tags?.length > 0
                ? tags.map((t) => <span key={t} className={`${tagBase} ${tagActive}`}>{t}</span>)
                : <p className="text-sm text-warm-400">Nenhuma especialidade informada.</p>
            }
          </div>
        </CardContent>
        </Card>

        {/* Serviços */}
        <Card id="baba-profile-services-card">
          <CardHeader><CardTitle>O que eu ofereço</CardTitle></CardHeader>
          <CardContent>
          <div className="mb-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-warm-500">Serviços básicos</p>
            <div className="flex flex-wrap gap-2">
              {BABYSITTER_BASIC_SERVICES.map((s) => (
                <span key={s} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{s}</span>
              ))}
            </div>
          </div>

          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-warm-500">Serviços extras</p>
          <div className="flex flex-wrap gap-2">
            {isEditing
              ? servicosOpcoes.map((s) => (
                  <span key={s} onClick={() => toggleArrayItem("servicos_extras", s)} className={`${tagBase} cursor-pointer ${perfilEdit.servicos_extras?.includes(s) ? tagActive : tagInactive}`}>{s}</span>
                ))
              : Array.isArray(perfil.servicos_extras) && perfil.servicos_extras.length > 0
                ? perfil.servicos_extras.map((s) => <span key={s} className={`${tagBase} ${tagActive}`}>{s}</span>)
                : <p className="text-sm text-warm-400">Nenhum serviço informado.</p>
            }
          </div>
        </CardContent>
        </Card>

        {/* Idiomas */}
        <Card id="baba-profile-languages-card">
          <CardHeader><CardTitle>Idiomas Falados</CardTitle></CardHeader>
          <CardContent>
          <div className="flex flex-wrap gap-2">
            {isEditing
              ? idiomasOpcoes.map((i) => (
                  <span key={i} onClick={() => toggleArrayItem("idiomas_falados", i)} className={`${tagBase} cursor-pointer ${perfilEdit.idiomas_falados?.includes(i) ? tagActive : tagInactive}`}>{i}</span>
                ))
              : Array.isArray(perfil.idiomas_falados) && perfil.idiomas_falados.length > 0
                ? perfil.idiomas_falados.map((i) => <span key={i} className={`${tagBase} ${tagActive}`}>{i}</span>)
                : <p className="text-sm text-warm-400">Nenhum idioma informado.</p>
            }
          </div>
        </CardContent>
        </Card>

        {/* Disponibilidade */}
        <Card id="baba-profile-availability-card">
          <CardHeader><CardTitle>Disponibilidade</CardTitle></CardHeader>
          <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left py-2 pr-4 text-xs text-warm-400 font-medium w-16"></th>
                  {periodosTrabalho.map((p) => (
                    <th key={p.key} className="text-center py-2 px-2 text-xs text-warm-500 font-medium">{p.label}</th>
                  ))}
                  <th className="text-center py-2 px-2 text-xs text-warm-500 font-medium">Todos</th>
                </tr>
              </thead>
              <tbody>
                {diasSemana.map((dia) => {
                  const estadoDia = isEditing ? disponibilidade[dia.key] || {} : perfil.disponibilidade?.[dia.key] || {};
                  const todosMarcados = periodosTrabalho.every((p) => estadoDia[p.key]);
                  return (
                    <tr key={dia.key} className="border-t border-warm-100">
                      <td className="py-2 pr-4 text-xs font-semibold text-warm-600">{dia.label}</td>
                      {periodosTrabalho.map((periodo) => {
                        const ativo = estadoDia[periodo.key];
                        return (
                          <td key={periodo.key} className="text-center py-2 px-2" onClick={isEditing ? () => toggleDisponibilidade(dia.key, periodo.key) : undefined} style={{ cursor: isEditing ? "pointer" : "default" }}>
                            <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border-2 transition-colors ${ativo ? "bg-rose-400 border-rose-400 text-white" : "border-warm-300 text-transparent"}`}>
                              <Check size={13} strokeWidth={3} />
                            </span>
                          </td>
                        );
                      })}
                      <td className="text-center py-2 px-2" onClick={isEditing ? () => toggleDiaTodo(dia.key) : undefined} style={{ cursor: isEditing ? "pointer" : "default" }}>
                        <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border-2 transition-colors ${todosMarcados ? "bg-slate-blue-400 border-slate-blue-400 text-white" : "border-warm-300 text-transparent"}`}>
                          <Check size={13} strokeWidth={3} />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
        </Card>

        {/* Rede Social */}
        <Card id="baba-profile-social-card">
          <CardHeader><CardTitle>Rede Social</CardTitle></CardHeader>
          <CardContent>
          {isEditing
            ? <input id="baba-profile-input-social" className={inputCls} name="rede_social" value={perfilEdit.rede_social || ""} onChange={handleEditChange} placeholder="Link Instagram/LinkedIn" />
            : perfil.rede_social
              ? <a id="baba-profile-link-social" href={perfil.rede_social} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-blue-400 hover:underline break-all">{perfil.rede_social}</a>
              : <p className="text-sm text-warm-400">Não informada.</p>
          }
        </CardContent>
        </Card>

        {/* Certificados */}
        <Card id="baba-profile-certs-card">
          <CardHeader><CardTitle>Certificados</CardTitle></CardHeader>
          <CardContent>
          <div className="space-y-2">
            {certificados?.map((c, i) => (
              <div key={i} className={isEditing ? "flex items-center gap-2" : ""}>
                {isEditing ? (
                  <>
                    <input className={`${inputCls} flex-1`} value={c.nome || ""} onChange={(e) => handleCertificateEdit(i, "nome", e.target.value)} placeholder="Nome do curso/certificado" />
                    {!c.url && (
                      <>
                        <input id={`cert-file-${c.id}`} type="file" accept="application/pdf,image/*" onChange={(e) => handleCertificateFileChange(c.id, e.target.files[0])} hidden />
                        <button type="button" onClick={() => document.getElementById(`cert-file-${c.id}`).click()} className="p-2 rounded-md border border-warm-300 hover:bg-warm-50" title="Escolher arquivo"><Upload size={15} className="text-warm-500" /></button>
                      </>
                    )}
                    {(c.url || c.previewUrl) && (
                      <a href={c.url || c.previewUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-md border border-emerald-600 text-emerald-600" title="Ver documento"><Check size={15} /></a>
                    )}
                    <button type="button" onClick={() => handleRemoveCertificateField(c.id)} className="p-2 rounded-md border border-red-100 text-red-600"><Trash2 size={15} /></button>
                  </>
                ) : (
                  (c.url || c.previewUrl) && (
                    <div className="flex items-center justify-between py-2 border-b border-warm-100 last:border-0">
                      <span className="text-sm font-medium text-warm-800">{c.nome || "Certificado"}</span>
                      <a href={c.url || c.previewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-blue-400 hover:underline">Ver documento →</a>
                    </div>
                  )
                )}
              </div>
            ))}
            {isEditing && (
              <Button size="sm" variant="outline" onClick={handleAddCertificateField}><Upload size={14} />Adicionar certificado</Button>
            )}
          </div>
        </CardContent>
        </Card>

        {/* Referências */}
        <Card id="baba-profile-refs-card">
          <CardHeader><CardTitle>Referências</CardTitle></CardHeader>
          <CardContent>
          <div className="space-y-3">
            {[0, 1].map((idx) => (
              <div key={idx}>
                {isEditing ? (
                  <div className="flex gap-2">
                    <input className={`${inputCls} flex-1`} value={perfilEdit.referencias_dados?.[idx]?.nome || ""} onChange={(e) => handleReferenceChange(idx, "nome", e.target.value)} placeholder={`Nome Referência ${idx + 1}`} />
                    <input className={`${inputCls} flex-1`} value={perfilEdit.referencias_dados?.[idx]?.contato || ""} onChange={(e) => handleReferenceChange(idx, "contato", e.target.value)} placeholder="Telefone/WhatsApp" />
                  </div>
                ) : (
                  perfil.referencias_dados?.[idx] && (
                    <p className="text-sm text-warm-700">
                      <span className="font-medium">{perfil.referencias_dados[idx].nome}</span> — {perfil.referencias_dados[idx].contato}
                    </p>
                  )
                )}
              </div>
            ))}
          </div>
        </CardContent>
        </Card>

        {/* Dados Bancários */}
        <Card id="baba-profile-bank-card">
          <CardHeader><CardTitle>Dados Bancários</CardTitle></CardHeader>
          <CardContent>
          {isEditing ? (
            <div className="grid grid-cols-2 gap-3">
              {[["Banco","banco"],["Agência","agencia"],["Conta","conta"],["PIX","pix"]].map(([label, name]) => (
                <div key={name}><label className="text-xs font-medium text-warm-600 mb-1 block">{label}</label><input className={inputCls} name={name} value={perfilEdit[name] || ""} onChange={handleEditChange} /></div>
              ))}
            </div>
          ) : (
            <dl className="grid grid-cols-2 gap-3 text-sm">
              {[["Banco", perfil.banco], ["Agência", perfil.agencia], ["Conta", perfil.conta], ["PIX", perfil.pix]].map(([label, val]) => (
                <div key={label}><dt className="text-xs text-warm-400 mb-0.5">{label}</dt><dd className="font-medium text-warm-800">{val || "Não informado"}</dd></div>
              ))}
            </dl>
          )}
        </CardContent>
        </Card>

        {/* Meus documentos */}
        <Card id="baba-profile-docs-card">
          <CardHeader><CardTitle>Meus documentos</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3 text-xs text-warm-500">
              Documentos enviados no cadastro. A selfie é a sua foto de perfil.
            </p>
            <DocumentsViewer user={perfil} role="baba" />
          </CardContent>
        </Card>
      </div>
    );
  };

  // =============================
  // EXTRATO FINANCEIRO (GANHOS E RETIRADAS)
  // =============================
  const renderExtratoBaba = () => {
    const transacoes = [...extratoUnificadoBaba].sort(
      (a, b) => new Date(b.criado_em) - new Date(a.criado_em),
    );

    const totalGanhos = transacoes
      .filter((x) => x.tipo_evento === "pagamento_servico")
      .reduce((acc, curr) => acc + (Number(curr.valor) || 0), 0);

    const totalRetiradas = transacoes
      .filter((x) => x.tipo_evento === "retirada")
      .reduce((acc, curr) => acc + (Number(curr.valor) || 0), 0);

    const saldoFinal = totalGanhos - totalRetiradas;

    return (
      <div id="baba-view-statement" className="space-y-4">

        {/* Cards de resumo */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Total ganhos", value: formatCurrency(totalGanhos), colorClass: "text-emerald-600", bgClass: "bg-emerald-100" },
            { label: "Total retiradas", value: formatCurrency(totalRetiradas), colorClass: "text-red-600", bgClass: "bg-red-100" },
            { label: "Saldo disponível", value: formatCurrency(saldoFinal), colorClass: "text-slate-blue-400", bgClass: "bg-slate-blue-100", bold: true },
          ].map(({ label, value, colorClass, bgClass, bold }) => (
            <Card key={label} className={`col-span-3 sm:col-span-1 ${bgClass}`}>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-1">{label}</p>
                <p className={`text-lg ${bold ? "font-bold" : "font-semibold"} ${colorClass}`}>{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabela de transações */}
        <Card className="py-0">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h3 className="font-heading font-semibold text-foreground">Extrato de Movimentações</h3>
            <Button size="sm" variant="ghost" onClick={() => window.print()}><Download size={14} />Imprimir</Button>
          </div>

          {transacoes.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-warm-400">Seu extrato de movimentação está vazio.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-warm-100">
                    {["Data", "Descrição", "Tipo", "Valor"].map((h, i) => (
                      <th key={h} className={`px-5 py-3 text-xs font-semibold text-warm-500 uppercase tracking-wide ${i >= 2 ? "text-center" : "text-left"} ${i === 3 ? "text-right" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transacoes.map((t) => {
                    const isCredit = t.tipo_evento === "pagamento_servico";
                    let descricao = "";
                    if (t.tipo_evento === "pagamento_servico") descricao = `Serviço para ${t.detalhe?.familia_nome || "Família"}`;
                    else if (t.tipo_evento === "retirada") descricao = t.detalhe?.descricao || "Solicitação de Saque";
                    else descricao = t.detalhe?.descricao || "Movimentação";

                    return (
                      <tr key={t.evento_id} className="border-b border-warm-50 hover:bg-warm-50 transition-colors">
                        <td className="px-5 py-3 text-warm-500 whitespace-nowrap">{new Date(t.criado_em).toLocaleDateString()}</td>
                        <td className="px-5 py-3">
                          <p className="font-medium text-warm-800">{descricao}</p>
                          <p className="text-xs text-warm-400">Status: {t.status}</p>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${isCredit ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                            {isCredit ? "Entrada" : "Saída"}
                          </span>
                        </td>
                        <td className={`px-5 py-3 text-right font-semibold tabular-nums ${isCredit ? "text-emerald-600" : "text-red-600"}`}>
                          {isCredit ? "+" : "−"} {formatCurrency(t.valor)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-5 py-3 border-t border-warm-100 text-xs text-warm-400">
            Tesourinhos Tecnologia — documento gerado em {new Date().toLocaleDateString()} às {new Date().toLocaleTimeString()}
          </div>
        </Card>
      </div>
    );
  };

  const renderAssinaturas = () => (
    <div id="baba-view-plans" className="space-y-4">
      <div>
        <h2 className="text-xl font-heading font-bold text-foreground">Planos e Assinaturas</h2>
        <p className="text-sm text-muted-foreground mt-1">Escolha o plano ideal para expandir sua presença na plataforma.</p>
      </div>
      <CompraPlanos userId={perfil.id} />
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "inicio":
        return renderInicio();
      case "perfil":
        return renderPerfil();
      case "faturamento":
        return renderExtratoBaba(); // Use the new extract component
      case "assinaturas":
        return renderAssinaturas();
      default:
        return null;
    }
  };

  // Passa a URL de display para o AppShell via perfil
  const perfilParaShell = perfil
    ? { ...perfil, foto_url_display: displayFotoUrl }
    : null;

  return (
    <AppShell
      role="baba"
      perfil={perfilParaShell}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      {renderContent()}

      {/* MODAIS GLOBAIS (CENTRALIZADOS) */}

      {/* Popup do Verificador de Identidade */}
      <Modal
        isOpen={isVerificadorOpen}
        onClose={() => setIsVerificadorOpen(false)}
        title="Verificação de Segurança"
      >
        <VerificadorIdentidade
          hideHeader={true}
          onClose={() => setIsVerificadorOpen(false)}
        />
      </Modal>

      {/* Popup de Bloqueio de Agenda */}
      <Modal
        isOpen={isBlockModalOpen}
        onClose={() => setIsBlockModalOpen(false)}
        title="Gerenciar Agenda"
        subtitle={selectedDate?.toLocaleDateString("pt-BR", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        })}
      >
        <div className="block-options">
          <p className="block-instruction">
            Selecione o período que deseja <strong>bloquear</strong> para novas
            reservas:
          </p>

          <div className="block-btn-grid">
            <button
              onClick={() => handleConfirmBlock("full")}
              className="btn-block-opt"
            >
              📅 Dia Inteiro (08h - 22h)
            </button>
            <button
              onClick={() => handleConfirmBlock("manha")}
              className="btn-block-opt"
            >
              ☀️ Manhã (08h - 12h)
            </button>
            <button
              onClick={() => handleConfirmBlock("tarde")}
              className="btn-block-opt"
            >
              🌅 Tarde (12h - 18h)
            </button>
            <button
              onClick={() => handleConfirmBlock("noite")}
              className="btn-block-opt"
            >
              🌙 Noite (18h - 22h)
            </button>
            <button
              onClick={() => handleConfirmBlock("pernoite")}
              className="btn-block-opt"
            >
              🌑 Pernoite (22h - 08h)
            </button>
          </div>

          <div className="existing-blocks">
            <h4>Bloqueios Manuais neste dia:</h4>
            {trabalhos.filter(
              (t) =>
                t.familia_id === t.baba_id &&
                new Date(t.inicio).toDateString() ===
                  selectedDate?.toDateString(),
            ).length > 0 ? (
              trabalhos
                .filter(
                  (t) =>
                    t.familia_id === t.baba_id &&
                    new Date(t.inicio).toDateString() ===
                      selectedDate?.toDateString(),
                )
                .map((b) => (
                  <div key={b.id} className="mini-block-item">
                    <span>
                      {new Date(b.inicio).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      -{" "}
                      {new Date(b.fim).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <button
                      onClick={() => handleCancelarAgendamento(b.id)}
                      className="btn-del-block"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
            ) : (
              <p className="no-blocks">Nenhum bloqueio para este dia.</p>
            )}
          </div>
        </div>
      </Modal>

      {/* Popup de Detalhes do Agendamento (Babá) */}
      <Modal
        isOpen={!!eventoSelecionado}
        onClose={() => setEventoSelecionado(null)}
        title="Detalhes do Compromisso"
        subtitle={
          eventoSelecionado?.familia_id === eventoSelecionado?.baba_id
            ? "Bloqueio de Agenda"
            : "Reserva de Família"
        }
      >
        {eventoSelecionado && (
          <div>
            <p>
              <strong>Cliente:</strong>{" "}
              {eventoSelecionado.title.replace("🏡 ", "")}
            </p>
            <p>
              <strong>Data:</strong>{" "}
              {eventoSelecionado.start.toLocaleDateString()}
            </p>
            <p>
              <strong>Horário:</strong>{" "}
              {eventoSelecionado.start.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              -{" "}
              {eventoSelecionado.end.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              <span className={`status-badge ${eventoSelecionado.status}`}>
                {eventoSelecionado.status === "agendado"
                  ? "Solicitado"
                  : eventoSelecionado.status}
              </span>
            </p>
            <p>
              <strong>Endereço:</strong>
              {familyDetails
                ? ` ${familyDetails.endereco || ""}, ${
                    familyDetails.numero || "S/N"
                  }${
                    familyDetails.complemento
                      ? ` (${familyDetails.complemento})`
                      : ""
                  } - ${familyDetails.bairro || ""}, ${
                    familyDetails.cidade || ""
                  } - ${familyDetails.estado || ""}`
                : " Carregando endereço..."}
            </p>

            <div className="flex flex-wrap justify-center gap-2.5 mt-7">
              {eventoSelecionado.familia_id === eventoSelecionado.baba_id ? (
                <button
                  className="rounded-xl bg-rose-400 px-5 py-2.5 text-sm font-semibold text-white hover:brightness-105 transition"
                  onClick={() => {
                    handleCancelarAgendamento(eventoSelecionado.id);
                    setEventoSelecionado(null);
                  }}
                >
                  Desbloquear Horário
                </button>
              ) : (
                <>
                  {eventoSelecionado.status === "agendado" && (
                    <>
                      <button
                        className="rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600 transition"
                        onClick={() => {
                          handleConfirmarTrabalho(eventoSelecionado.id);
                          setEventoSelecionado(null);
                        }}
                      >
                        Aceitar
                      </button>
                      <button
                        className="rounded-xl bg-red-400 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-500 transition"
                        onClick={() => {
                          handleRecusarTrabalho(eventoSelecionado.id);
                          setEventoSelecionado(null);
                        }}
                      >
                        Recusar
                      </button>
                    </>
                  )}
                  {eventoSelecionado.status === "confirmado" && (
                    <button
                      className="rounded-xl bg-rose-400 px-5 py-2.5 text-sm font-semibold text-white hover:brightness-105 transition"
                      onClick={() => {
                        handleCancelarAgendamento(eventoSelecionado.id);
                        setEventoSelecionado(null);
                      }}
                    >
                      Cancelar Trabalho
                    </button>
                  )}
                </>
              )}
              {eventoSelecionado?.familia_id !== eventoSelecionado?.baba_id && (
                <button
                  className="rounded-xl border border-warm-300 px-5 py-2.5 text-sm font-semibold text-warm-700 hover:bg-warm-100 transition"
                  onClick={() =>
                    navigate(`/perfil-familia/${eventoSelecionado.familia_id}`)
                  }
                >
                  Ver Perfil da Família
                </button>
              )}
              <button
                className="rounded-xl border border-warm-300 px-5 py-2.5 text-sm font-semibold text-warm-700 hover:bg-warm-100 transition"
                onClick={() => setEventoSelecionado(null)}
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </Modal>
    </AppShell>
  );
}
